// When making a change to the limitations, the following files must be updated to reflect the changed values:
// https://livegrep.corp.stripe.com/view/stripe-internal/pay-server/docs/content/connect/partials/connect-embedded-components/customize.md
// https://livegrep.corp.stripe.com/view/stripe-internal/pay-server/frontend/embedded-theming/src/AppearanceOptions.ts
// https://github.com/stripe/connect-js/blob/master/types/shared.d.ts
// The range of valid values for each of the numeric options
// for radius and spacing, only px are supported
export const numericOptionLimits = {
    fontWeight: {
        min: 100,
        max: 1000
    },
    radius: {
        min: 0,
        max: 24
    },
    spacing: {
        min: 8,
        max: 20
    }
};
// The minimum value for each of the padding options (based on default scaling of spacingUnit)
// Padding min values differ between components and whether they are horizontal or vertical
// Only px are supported
export const paddingOptionLimits = {
    buttonPaddingX: {
        min: 8
    },
    buttonPaddingY: {
        min: 4
    },
    badgePaddingX: {
        min: 6
    },
    badgePaddingY: {
        min: 2
    },
    inputFieldPaddingX: {
        min: 8
    },
    inputFieldPaddingY: {
        min: 4
    },
    tableRowPaddingY: {
        min: 8
    }
};
export const fontSizeOptionLimits = {
    fontSize: {
        px: {
            min: 1,
            max: 200
        },
        em: {
            min: 0.1,
            max: 12
        },
        rem: {
            min: 0.1,
            max: 12
        },
        other: {
            min: 1,
            max: 200
        }
    },
    fontSizeBase: {
        px: {
            min: 1,
            max: 40
        },
        em: {
            min: 0.1,
            max: 4
        },
        rem: {
            min: 0.1,
            max: 4
        },
        other: {
            min: 1,
            max: 200
        }
    }
};
// These values map to https://developer.mozilla.org/en-US/docs/Web/CSS/font-weight
export const fontWeightMap = {
    lighter: 300,
    normal: 400,
    bold: 700,
    bolder: 900
};
export const parseFontWeight = (fontWeight)=>{
    const fontWeightAsNumber = parseInt(fontWeight, 10);
    if (isNaN(fontWeightAsNumber)) {
        const calculatedFontWeight = fontWeightMap[fontWeight.toLowerCase()];
        return calculatedFontWeight; // This will be undefined if it is an invalid value
    }
    return fontWeightAsNumber;
};
export const calculateSubduedFontWeight = (weight)=>{
    const parsedFontWeight = parseFontWeight(weight);
    if (!parsedFontWeight) return undefined;
    return Math.max(parsedFontWeight - 300, 100).toString();
};
export const calculateEmphasizedFontWeight = (weight)=>{
    const parsedFontWeight = parseFontWeight(weight);
    if (!parsedFontWeight) return undefined;
    return Math.min(parsedFontWeight + 200, 900).toString();
};
export const getTokenNumericValue = (tokenValue, tokenUnit)=>{
    return parseFloat(tokenValue.slice(0, tokenValue.length - tokenUnit.length));
};
export const scaleTokenNumber = (tokenValue, tokenUnit, scale)=>{
    if (!tokenValue) {
        return undefined;
    }
    const scaledValue = getTokenNumericValue(tokenValue, tokenUnit) * scale;
    return scaledValue + tokenUnit;
};
export const parseFontSizeUnit = (fontSize)=>{
    const fontSizeMatch = fontSize.match(/(px|em|rem|pt|pc|in|cm|mm|ex|ch|q|vw|vh|vmin|vmax|%)$/);
    if (!fontSizeMatch) {
        return undefined;
    }
    return fontSizeMatch[1];
};
// only px, em, and rem are supported for font size base units.
export const isSupportedFontSizeBaseUnit = (fontUnit)=>{
    if (!(fontUnit === 'px' || fontUnit === 'em' || fontUnit === 'rem')) {
        return false;
    }
    return true;
};
export const getLineHeight = (fontSize, increment)=>{
    if (!fontSize) {
        return undefined;
    }
    const parsedFontSizeUnit = parseFontSizeUnit(fontSize);
    if (parsedFontSizeUnit === 'em') {
        // for ems, we need to add the increment to the base font size
        return `calc(1em + ${increment}px)`;
    }
    if (parsedFontSizeUnit !== 'px') {
        return `calc(${fontSize} + ${increment}px)`;
    }
    return `${parseInt(fontSize.toString().slice(0, fontSize.toString().length - parsedFontSizeUnit.length), 10) + increment}${parsedFontSizeUnit}`;
};
export const calculateFontSizeRatio = (fontSizeBase, fontSize, tokenUnit)=>{
    const fontSizeBaseValue = getTokenNumericValue(fontSizeBase, tokenUnit);
    const fontSizeValue = getTokenNumericValue(fontSize, tokenUnit);
    return fontSizeValue / fontSizeBaseValue;
};
export const getScaledFontSize = (fontSizeBase, tokenUnit, scale)=>{
    if (!fontSizeBase || !tokenUnit) {
        return undefined;
    }
    if (scale <= 0) {
        return undefined;
    }
    const scaledValue = getTokenNumericValue(fontSizeBase, tokenUnit) * scale;
    return `${scaledValue}${tokenUnit}`;
};

//# sourceMappingURL=tokenUtils.js.map